import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { newsArticles } from '../../data/news';
import './NewsArticle.css';

const NewsArticle = () => {
  const { newsId } = useParams();
  const article = newsArticles.find(a => a.id === newsId);

  if (!article) {
    return (
      <div className="page-content">
        <section className="section">
          <div className="article-not-found">
            <h1>Article Not Found</h1>
            <p>The article you're looking for doesn't exist.</p>
            <Link to="/news" className="back-link">
              ← Back to News
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Get related articles (same category, exclude current)
  const relatedArticles = newsArticles
    .filter(a => a.category === article.category && a.id !== article.id)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  const sanitizedContent = useMemo(
    () => DOMPurify.sanitize(article.content),
    [article.content]
  );

  return (
    <div className="page-content">
      {/* Article Header */}
      <section className="section">
        <div className="article-container">
          <div className="article-back-link">
            <Link to="/news" className="back-link-button">
              <span className="material-icons">arrow_back</span>
              Back to News
            </Link>
          </div>

          <div className="article-category-badge">
            {article.category}
          </div>

          <h1 className="article-title">
            {article.title}
          </h1>

          <div className="article-meta">
            <div className="article-meta-item">
              <span className="material-icons">calendar_today</span>
              <span>{formatDate(article.date)}</span>
            </div>
            <div className="article-meta-item">
              <span className="material-icons">person</span>
              <span>{article.author}</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="article-featured-image">
            <div className="article-image-placeholder">
              <span className="material-icons">article</span>
            </div>
          </div>

          {/* Article Content */}
          <div 
            className="article-content"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />

        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="section related-articles-section">
          <div className="container">
            <h2 className="related-articles-title">Related News</h2>
            <div className="related-articles-grid">
              {relatedArticles.map((relatedArticle) => (
                <Link 
                  key={relatedArticle.id}
                  to={`/news/${relatedArticle.id}`}
                  className="related-article-link"
                >
                  <div className="related-article-card">
                    <div className="related-article-image">
                      <div className="related-article-icon">
                        <span className="material-icons">article</span>
                      </div>
                      <div className="related-article-badge">
                        {relatedArticle.category}
                      </div>
                    </div>
                    <div className="related-article-content">
                      <div className="related-article-date">
                        {formatDate(relatedArticle.date)}
                      </div>
                      <h3 className="related-article-title">
                        {relatedArticle.title}
                      </h3>
                      <div className="related-article-link-text">
                        <span>Read More</span>
                        <span className="material-icons">arrow_forward</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsArticle;

